import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, ScrollText, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p || 0);

export default function InvoicePage() {
  const { proposalId } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [sigName, setSigName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/public/invoice/${proposalId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setInv)
      .catch(() => setError('Invoice not found'));
  }, [proposalId]);

  const pay = async (type) => {
    setPaying(true);
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposal_id: proposalId, origin_url: window.location.origin, payment_type: type }),
      });
      const d = await res.json();
      if (d.checkout_url) window.location.href = d.checkout_url;
      else { setError(d.detail || 'Could not start checkout'); setPaying(false); }
    } catch {
      setError('Could not start checkout');
      setPaying(false);
    }
  };

  const sign = async () => {
    setSignError('');
    setSigning(true);
    try {
      const res = await fetch(`${API}/api/contracts/${proposalId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_name: sigName, agreed }),
      });
      const d = await res.json();
      if (!res.ok) { setSignError(d.detail || 'Could not sign'); }
      else setInv(v => ({ ...v, signed: true, esign: d.esign }));
    } catch { setSignError('Could not sign — try again'); }
    setSigning(false);
  };

  if (error) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <p className="text-white/60 text-center" data-testid="invoice-error">{error}</p>
    </div>
  );
  if (!inv) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-gold animate-spin" data-testid="invoice-loading" />
    </div>
  );

  const paidInFull = inv.balance <= 0 && inv.paid > 0;
  const line = (i) => (i.price || 0) * (i.quantity || 1) + (i.additionalHourPrice || 0) * ((i.hours || 1) - 1);

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-10 px-5" data-testid="invoice-page" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <p className="text-gold text-[10px] uppercase tracking-[0.4em] mb-2">{inv.business.tagline}</p>
          <h1 className="text-white text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>{inv.business.name}</h1>
        </div>

        <div className="bg-white/[0.03] border border-white/10 rounded-3xl overflow-hidden">
          <div className="px-7 pt-7 pb-5 border-b border-white/5 flex items-start justify-between">
            <div>
              <p className="text-white/30 text-[10px] uppercase tracking-[0.25em] mb-1">Invoice</p>
              <p className="text-white text-lg font-semibold" data-testid="invoice-number">{inv.invoice_number}</p>
            </div>
            <div className="text-right">
              <p className="text-white/30 text-[10px] uppercase tracking-[0.25em] mb-1">Billed To</p>
              <p className="text-white text-sm">{inv.client_name}</p>
              {inv.event_date && <p className="text-white/40 text-xs mt-0.5">{inv.event_type || 'Event'} · {inv.event_date}{inv.event_time ? ` · ${inv.event_time}` : ''}</p>}
            </div>
          </div>

          <div className="px-7 py-6">
            <div className="space-y-3.5" data-testid="invoice-items">
              {inv.items.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-white/70">{i.name}{i.quantity > 1 ? ` × ${i.quantity}` : ''}</span>
                  <span className="text-white/50 tabular-nums">{fmt(line(i))}</span>
                </div>
              ))}
              {inv.delivery_fee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-white/70">Delivery &amp; Setup</span>
                  <span className="text-white/50 tabular-nums">{fmt(inv.delivery_fee)}</span>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 mt-6 pt-5 space-y-2.5">
              <div className="flex justify-between">
                <span className="text-white/50 text-sm">Total</span>
                <span className="text-white text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="invoice-page-total">{fmt(inv.total)}</span>
              </div>
              {inv.paid > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Paid</span>
                  <span className="text-emerald-400 tabular-nums">−{fmt(inv.paid)}</span>
                </div>
              )}
              {inv.paid > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Balance</span>
                  <span className="text-gold font-semibold tabular-nums" data-testid="invoice-page-balance">{fmt(inv.balance)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="px-7 pb-7">
            {paidInFull ? (
              <div className="flex items-center justify-center gap-2 py-4 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-semibold" data-testid="invoice-paid-badge">
                <CheckCircle2 className="w-4 h-4" /> Paid in Full — Thank You
              </div>
            ) : !inv.deposit_paid ? (
              <button onClick={() => pay('deposit')} disabled={paying}
                      className="w-full py-4 rounded-full bg-gold text-black text-sm font-bold hover:bg-[#E6BE5A] disabled:opacity-60 transition-colors" data-testid="invoice-pay-btn">
                {paying ? 'Opening secure checkout…' : `Pay Deposit ${fmt(inv.deposit_due)} — Reserve Your Date`}
              </button>
            ) : (
              <button onClick={() => pay('balance')} disabled={paying}
                      className="w-full py-4 rounded-full bg-gold text-black text-sm font-bold hover:bg-[#E6BE5A] disabled:opacity-60 transition-colors" data-testid="invoice-pay-btn">
                {paying ? 'Opening secure checkout…' : `Pay Remaining Balance ${fmt(inv.balance)}`}
              </button>
            )}
            <p className="text-white/25 text-[11px] text-center mt-4">Secure payment powered by Stripe · {inv.business.location}</p>
          </div>
        </div>

        {/* Service Agreement — part of the invoice */}
        {inv.agreement_text && (
          <div className="bg-white/[0.03] border border-white/10 rounded-3xl overflow-hidden mt-5" data-testid="invoice-agreement">
            <div className="px-7 pt-6 pb-5 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <ScrollText className="w-5 h-5 text-gold flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-semibold">Service Agreement</p>
                  <p className="text-white/35 text-xs mt-0.5">Part of this invoice</p>
                </div>
              </div>
              {inv.signed && (
                <span className="flex items-center gap-1.5 text-emerald-400 text-xs font-semibold bg-emerald-500/10 px-3 py-1.5 rounded-full flex-shrink-0" data-testid="agreement-signed-badge">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Signed
                </span>
              )}
            </div>

            <button onClick={() => setTermsOpen(!termsOpen)}
                    className="w-full flex items-center justify-between px-7 py-3 border-t border-white/5 text-white/50 text-xs"
                    data-testid="agreement-toggle">
              {termsOpen ? 'Hide full terms' : 'Read full terms'}
              <ChevronDown className={`w-4 h-4 transition-transform ${termsOpen ? 'rotate-180' : ''}`} />
            </button>
            {termsOpen && (
              <div className="px-7 pb-5 max-h-80 overflow-y-auto">
                <pre className="text-white/45 text-[11.5px] leading-relaxed whitespace-pre-wrap font-sans" data-testid="agreement-text">{inv.agreement_text}</pre>
              </div>
            )}

            <div className="px-7 pb-7 pt-2 border-t border-white/5">
              {inv.signed ? (
                <p className="text-white/40 text-xs pt-4" data-testid="agreement-signed-detail">
                  Signed by <span className="text-white/80 font-semibold">{inv.esign?.signature_name}</span>
                  {inv.esign?.signed_at ? ` on ${new Date(inv.esign.signed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` : ''}
                </p>
              ) : (
                <div className="pt-4 space-y-4">
                  {signError && <p className="text-red-400 text-xs" data-testid="agreement-error">{signError}</p>}
                  <input value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder="Type your full legal name"
                         className="w-full bg-black/30 border border-white/15 rounded-2xl px-4 py-3.5 text-white text-sm focus:outline-none focus:border-gold placeholder:text-white/25"
                         style={{ fontFamily: 'Playfair Display, serif', fontStyle: sigName ? 'italic' : 'normal' }}
                         data-testid="agreement-signature-input" />
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                           className="mt-0.5 w-4 h-4 accent-[#D4A843]" data-testid="agreement-checkbox" />
                    <span className="text-white/50 text-xs leading-relaxed">I have read and agree to the Exotic Animal Agreement above. Typing my name constitutes my electronic signature.</span>
                  </label>
                  <button onClick={sign} disabled={signing || !sigName.trim() || !agreed}
                          className="w-full py-3.5 rounded-full bg-white/10 border border-gold/40 text-gold text-sm font-bold hover:bg-gold/10 disabled:opacity-40 transition-colors"
                          data-testid="agreement-sign-btn">
                    {signing ? 'Signing…' : 'Sign Agreement'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
